"use client";

import type { LessonDetails } from "@/features/workspaces/types/lessonDetails.ts";

import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";

interface LessonMaterialsCardProps {
  lessonDetails: LessonDetails;
}

export const LessonMaterialsCard = ({}: LessonMaterialsCardProps) => {
  // Placeholder data for materials
  const materials = [
    { name: "Grammar Guide.pdf", size: "2.4 MB", type: "pdf", reviewed: true },
    {
      name: "Lesson Slides.pptx",
      size: "5.1 MB",
      type: "presentation",
      reviewed: false,
    },
    { name: "Audio Exercise.mp3", size: "8.7 MB", type: "audio", reviewed: true },
  ];

  const reviewedCount = materials.filter((m) => m.reviewed).length;
  const totalMaterials = materials.length;
  const progressPercentage = totalMaterials > 0 ? (reviewedCount / totalMaterials) * 100 : 0;
  const { t } = useTranslation(["workspaces", "common"]);

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="col-span-12 lg:col-span-7"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: 0.2 }}
    >
      <div className="bg-muted/20 text-accent-foreground rounded-2xl border-none p-4 shadow-2xl sm:p-6">
        <h3 className="mb-4 flex items-center gap-3 text-lg font-bold sm:mb-6 sm:text-xl">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-green-500/20 sm:h-10 sm:w-10">
            <Icon className="h-4 w-4 text-green-400 sm:h-5 sm:w-5" icon="mdi:file-document" />
          </div>
          {t("workspaces:lessonMaterials", "Lesson Materials")}
        </h3>

        <div className="mb-4 space-y-3 sm:mb-6">
          {materials.map((material, index) => (
            <motion.div
              key={index}
              animate={{ opacity: 1, x: 0 }}
              className="bg-muted/30 hover:bg-muted/40 flex items-center gap-3 rounded-xl p-3 backdrop-blur-sm transition-all sm:gap-4 sm:p-4"
              initial={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2, delay: index * 0.1 }}
            >
              <div className="bg-muted/40 flex h-10 w-10 items-center justify-center rounded-xl sm:h-12 sm:w-12">
                <Icon
                  className="text-accent-foreground h-5 w-5 sm:h-6 sm:w-6"
                  icon={
                    material.type === "pdf"
                      ? "mdi:file-pdf-box"
                      : material.type === "presentation"
                        ? "mdi:file-powerpoint-box"
                        : "mdi:file-music"
                  }
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium sm:text-base">{material.name}</p>
                <p className="text-muted-foreground text-xs sm:text-sm">{material.size}</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                {material.reviewed ? (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500/20 sm:h-8 sm:w-8">
                    <Icon
                      className="h-3 w-3 text-green-400 sm:h-4 sm:w-4"
                      icon="mdi:check-circle"
                    />
                  </div>
                ) : (
                  <Button
                    className="rounded-lg bg-blue-500/20 px-2 py-1 text-xs text-blue-400 hover:bg-blue-500/30 sm:px-3"
                    size="sm"
                  >
                    {t("workspaces:review", "Review")}
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="bg-muted/40 hover:bg-muted/50 text-accent-foreground h-7 w-7 rounded-lg p-0 sm:h-8 sm:w-8"
                      variant="ghost"
                    >
                      <MoreHorizontal className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem>
                      <Icon className="mr-2 h-4 w-4" icon="mdi:download" />
                      {t("common:download", "Download")}
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Icon className="mr-2 h-4 w-4" icon="mdi:eye" />
                      {t("common:view", "View")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="bg-muted/30 rounded-xl p-3 backdrop-blur-sm sm:p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-accent-foreground text-sm font-medium sm:text-base">
              {t("workspaces:progress", "Progress")}
            </span>
            <span className="text-accent-foreground text-sm font-bold sm:text-base">
              {reviewedCount}/{totalMaterials}
            </span>
          </div>
          <div className="bg-muted/40 h-2 w-full rounded-full sm:h-3">
            <div
              className="h-2 rounded-full bg-green-400 transition-all duration-500 sm:h-3"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};
